import type { BadgeProps } from '@/shared/components/ui/badge'
import type { PricingCampaignModel } from '@/shared/generated'

import React, { useState } from 'react'
import { ChevronUpIcon, Maximize2Icon, Minimize2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { Header } from '@/shared/components/ui/layout'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'

type PricingAlertsSolutionProps = {
	name: string
	badgeContent: {
		formattedMetrics: {
			products: string
			revenue: string
		}
		badgeVariant: BadgeProps['variant']
	}
	solution: {
		description: string
		pricingCampaigns: PricingCampaignModel[] | null
		tooltip: string
		optimizationGroupId: string
	}
	optimizationGroupId?: string
}
export function PricingAlertsSolutionPopup({
	kind,
	alertsOf,
	badgeContent,
	name,
	solution,
}: {
	kind: string
	alertsOf: 'pc' | 'whatif' | 'og' | 'assortment' | 'anchoring'
} & PricingAlertsSolutionProps) {
	const [open, setOpen] = useState(false)
	const handleClick = () =>
		ampli.alertsViewSolutionClicked({
			alert_kind: kind,
			alerts_of: alertsOf,
		})
	const ButtonIcon = open ? Minimize2Icon : Maximize2Icon

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					size='icon-sm'
					variant='ghost'
					className='ml-2'
					onClick={handleClick}
				>
					<ButtonIcon />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' side='left' className='w-auto p-0'>
				<div className='max-h-96 w-[360px] overflow-hidden'>
					<PricingAlertsSolution
						badgeContent={badgeContent}
						name={name}
						solution={solution}
					/>
				</div>
			</PopoverContent>
		</Popover>
	)
}

const PricingAlertsSolution = ({
	name,
	badgeContent: { formattedMetrics, badgeVariant },
	solution,
}: PricingAlertsSolutionProps) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const { pricingCampaigns, tooltip, description, optimizationGroupId } =
		solution

	return (
		<div>
			<div className='border-b p-4'>
				<Header className='mb-1 font-semibold'>
					{t(name, { defaultValue: name })}
				</Header>
				{description && (
					<div className='mb-2 text-sm text-muted-foreground'>
						{t(description)}
					</div>
				)}
				<Badge variant={badgeVariant}>
					{t('alert.sku.number', { defaultValue: 'Number of SKUs' })}
					{': '}
					{formattedMetrics.products} ({t('value')}
					{': '}
					{formattedMetrics.revenue})
				</Badge>
			</div>
			<div className='max-h-44 overflow-auto'>
				<div className='space-y-2 border-b p-4 text-sm'>
					<div className='font-semibold'>
						{t('alert.solution.tooltip', { defaultValue: 'Solution(s)' })}
					</div>
					{tooltip && (
						<p
							dangerouslySetInnerHTML={{
								__html: t(tooltip, { defaultValue: tooltip }),
							}}
						/>
					)}
				</div>
				{!!pricingCampaigns?.length && (
					<Collapsible className='border-b p-4'>
						<div className='flex items-center justify-between'>
							<h5>
								{t('alert.pricing_compaign_tooltip', {
									defaultValue: 'Related pricing campaigns',
								})}
							</h5>
							<CollapsibleTrigger asChild>
								<ChevronUpIcon className='flex size-4 cursor-pointer items-center data-[state=closed]:rotate-180' />
							</CollapsibleTrigger>
						</div>
						<CollapsibleContent className='py-4'>
							{pricingCampaigns.map((pc: PricingCampaignModel) => (
								<div key={pc.id}>
									<LinkButton
										className='text-left'
										variant='brand'
										onClick={() => {
											ampli.settingsPricingCampaignsViewClick({
												location: 'alert-solution',
											})
											showModal('PRICING_CAMPAIGN_MODAL', {
												optimizationGroupId,
												pricingCampaignId: pc.id,
											})
										}}
									>
										{pc.name}
									</LinkButton>
								</div>
							))}
						</CollapsibleContent>
					</Collapsible>
				)}
				<div className='space-y-1 p-4 text-sm font-semibold'>
					<p>
						{t('alert.support.title', {
							defaultValue: 'Still have questions?',
						})}
					</p>
					<LinkButton
						variant='brand'
						onClick={() => showModal('CREATE_TICKET')}
					>
						{t('alert.support.button', {
							defaultValue: 'Contact our support team',
						})}
					</LinkButton>
				</div>
			</div>
		</div>
	)
}
